import { createContext, useContext, useState, useCallback } from "react";

const TournamentCreationContext = createContext(null);

const initialState = {
  // Step 1 - Competition Details
  name: "",
  startDate: "",
  endDate: "",
  dayStartTime: "09:00",
  dayEndTime: "21:00",
  logoFile: null,
  logoPreview: null,
  // Step 2 - Configuration
  sport: "",
  categories: [],
  // Step 3 - Venue
  venue: "",
  numberOfPitches: "",
  location: { lat: null, lng: null },
  mapLink: "",
  // Step 4 - Registration Fees per category (e.g. { "U10": "30", "U14": "25" })
  categoryFees: {},
};

export const TournamentCreationProvider = ({ children }) => {
  const [formData, setFormData] = useState(initialState);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateFields = useCallback((fields) => {
    setFormData((prev) => ({ ...prev, ...fields }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialState);
  }, []);

  const getCreatePayload = useCallback(
    (organisationId) => {
      const payload = {
        name: formData.name.trim(),
        organisationId,
      };

      if (formData.sport) {
        payload.sport = formData.sport;
      }

      if (formData.categories.length > 0) {
        payload.categories = formData.categories.map((cat) => {
          const category = { label: cat };
          const fee = formData.categoryFees[cat];
          if (fee !== undefined && fee !== "") {
            category.registrationFee = parseFloat(fee);
          }
          return category;
        });
      }

      if (formData.startDate || formData.endDate) {
        payload.matchSchedule = {};
        if (formData.startDate) {
          payload.matchSchedule.startDate = new Date(formData.startDate).toISOString();
        }
        if (formData.endDate) {
          payload.matchSchedule.endDate = new Date(formData.endDate).toISOString();
        }
        if (formData.dayStartTime) {
          payload.matchSchedule.startTime = formData.dayStartTime;
        }
        if (formData.dayEndTime) {
          payload.matchSchedule.dayEndTime = formData.dayEndTime;
        }
      }

      if (formData.venue || formData.numberOfPitches) {
        payload.matchVenue = {};
        if (formData.venue) payload.matchVenue.venue = formData.venue;
        if (formData.numberOfPitches) {
          payload.matchVenue.numberOfPitches = parseInt(formData.numberOfPitches);
        }
        if (formData.location.lat !== null && formData.location.lng !== null) {
          payload.matchVenue.location = formData.location;
          payload.matchVenue.mapLink =
            formData.mapLink ||
            `https://www.google.com/maps?q=${formData.location.lat},${formData.location.lng}`;
        }
      }

      return payload;
    },
    [formData]
  );

  return (
    <TournamentCreationContext.Provider
      value={{ formData, updateField, updateFields, resetForm, getCreatePayload }}
    >
      {children}
    </TournamentCreationContext.Provider>
  );
};

export const useTournamentCreation = () => {
  const context = useContext(TournamentCreationContext);
  if (!context) {
    throw new Error(
      "useTournamentCreation must be used within a TournamentCreationProvider"
    );
  }
  return context;
};
